import { useState, useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Box } from '@mui/material';
import { enqueueSnackbar } from 'notistack';
import CustomDataGrid from '../../../../reusable-components/datagrid/CustomDataGrid';
import { getPriceAccounts, deleteVendorPriceAccount } from '../../../../api/vendors';
import { VENDOR_PRICE_ACCOUNT } from '../../../../reusable-components/datagrid/vendorPriceAccountColumns';
import AddButton from '../../../../reusable-components/basic-buttons/AddButton';
import PriceAccountDialog from './PriceAccountDialog';
import { getIntegratedVendors } from '../../../../redux/slices/vendors';
import { ConfirmDialog } from '../../../../reusable-components/confirm-dialog';
import EditIconButton from '../../../../reusable-components/basic-buttons/EditIconButton';

export default function PriceAccountsView() {
  const dispatch = useDispatch();
  const { integratedVendors } = useSelector((state) => state.vendors.data);

  const [priceAccounts, setPriceAccounts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isPriceAccountDialogOpen, setIsPriceAccountDialogOpen] = useState(false);
  const [value, setValue] = useState({});


  const ActionButtons = (params) => {
    const [isDeleting, setIsDeleting] = useState(false);

    const openPriceAccountDialog = () => {
      setValue({
        vendorId: params.row.vendorId,
        accountNumber: params.row.accountNumber,
        description: params.row.description,
        id: params.row.id
      })
      setIsPriceAccountDialogOpen(true);
    }

    const deletePriceAccount = (id) => {
      setIsDeleting(true);
      deleteVendorPriceAccount(id)
        .then(() => {
          fetchPriceAccounts();
          enqueueSnackbar('Price account deleted', { variant: 'success' })
        })
        .catch(() => enqueueSnackbar('Failed to delete vendor price account', { variant: 'error' }))
        .finally(() => setIsDeleting(false));
    }

    return <Box>
      <EditIconButton onClick={openPriceAccountDialog} />
      <ConfirmDialog
        icon="delete-outline"
        title="Delete"
        color="error"
        content={`Are you sure you want to delete price account ${params.row.accountNumber}?`}
        actionButton="Delete"
        manualLoading={isDeleting}
        action={() => deletePriceAccount(params.row.id)}
      />
    </Box>;
  }

  const actions = {
    field: 'actions',
    headerName: 'Actions',
    disableReorder: true,
    minWidth: 120,
    renderCell: (params) => <ActionButtons {...params} />,
    id: 'actions',
    className: 'actions',
  };

  const fetchPriceAccounts = () => {
    setIsLoading(true);
    getPriceAccounts()
      .then((response) => setPriceAccounts(response.data))
      .catch(() => enqueueSnackbar('Failed to fetch vendor price accounts', { variant: 'error' }))
      .finally(() => setIsLoading(false));
  }

  useEffect(() => {
    if(!integratedVendors?.length) dispatch(getIntegratedVendors());
    fetchPriceAccounts();
  }, [])

  const vendorOptions = useMemo(() => (integratedVendors ?? [])
    .map((vendor) => ({ id: vendor.value, label: vendor.label }))
    .sort((a, b) => a.label.localeCompare(b.label)
  ), [integratedVendors]);
  
  const data = useMemo(() => priceAccounts.map((account) => ({
    ...account,
    vendor: integratedVendors?.find((vendor) => vendor.value === account.vendorId)?.label || '',
  })), [priceAccounts, integratedVendors]);

  return <>
    <CustomDataGrid
      data={data}
      gridId="price-accounts"
      isLoading={isLoading}
      gridColumns={[...VENDOR_PRICE_ACCOUNT, actions]}
      CustomLeftToolbar={() => <AddButton onClick={() => setIsPriceAccountDialogOpen(true)}>Add Vendor Price Account</AddButton>}
      disableCellHighlight
    />
    {isPriceAccountDialogOpen && <PriceAccountDialog
      setIsPriceAccountDialogOpen={setIsPriceAccountDialogOpen}
      isPriceAccountDialogOpen={isPriceAccountDialogOpen}
      onSuccess={fetchPriceAccounts}
      vendorOptions={vendorOptions}
      value={value}
      setValue={setValue}
      priceAccounts={data}
    />}
  </>
}